import React, { useState, useEffect, useMemo } from 'react';

import cx from 'classnames';
import { downloadChannelInteractions } from 'helpers';
import { Link } from 'react-router-dom';

import { pause } from 'async-timing';
import { SORT_START } from 'lane-shared/helpers/interactions/types';
import { useInteractionsOnChannelQuery } from 'lane-shared/hooks';
import { parseDate } from 'lane-shared/helpers/dates';

import Dropdown from '../form/Dropdown';
import ControlMenu from '../general/ControlMenu';
import ErrorMessage from '../general/ErrorMessage';
import IconButton from '../general/IconButton';
import Pagination from '../general/Pagination';
import { UCIList } from './UCIList';
import { UCITableV2 } from './UCITableV2';

import styles from './UCIs.scss';
import { SortDirectionEnum } from 'constants-activate';
import { getPageSizeFromQueryString } from 'design-system-web/components/Table/utils/queryStrings';
import { useQueryString, QueryObject } from 'lane-shared/hooks/useQueryString';

enum State {
  Any = 'any',
  Closed = 'closed',
  Open = 'open',
}

enum View {
  // Order matters; corresponds to order in UI
  List = 'list',
  Grid = 'th-large',
}

const stateDropdownItems = [
  {
    label: 'Any state',
    value: State.Any,
  },
  {
    label: 'Open',
    value: State.Open,
  },
  {
    label: 'Closed',
    value: State.Closed,
  },
];

const DEFAULT_PER_PAGE = 25;

type UCIQueryParams = QueryObject & {
  page: string;
  pageSize: string;
  sortBy: string;
  sortDirection: string;
  selectedState: string;
  view?: string;
  interactionsView?: string;
  keyword: string;
  status: string;
  dateRangeFilter: string;
  interactionCreationDates: string;
};

const validSortOrder = (key: any): key is SortDirectionEnum => {
  return Object.values(SortDirectionEnum).includes(key);
};

const parseStringOrDefault = (value: any, defaultValue: string) =>
  typeof value === 'string' ? value : defaultValue;

const parseNonEmptyString = (value: any) =>
  typeof value === 'string' && value !== '' ? value : undefined;

const getDatesFromDateRange = (
  daterange: string
): { date1: string | null; date2: string | null } => {
  if (daterange && daterange.indexOf('_') !== -1) {
    const [date1, date2] = daterange.split('_');

    return {
      date1,
      date2,
    };
  }

  return {
    date1: null,
    date2: null,
  };
};

const useDates = (query: UCIQueryParams) =>
  useMemo(() => {
    const dateRangeFilterValue = getDatesFromDateRange(query.dateRangeFilter);
    const creationDateRangeValue = getDatesFromDateRange(
      query.interactionCreationDates
    );

    return {
      startDate: parseDate(dateRangeFilterValue?.date1),
      endDate: parseDate(dateRangeFilterValue?.date2),
      startCreationDate: parseDate(creationDateRangeValue?.date1),
      endCreationDate: parseDate(creationDateRangeValue?.date2),
    };
  }, [query.dateRangeFilter, query.interactionCreationDates]);

const useParseQueryValues = (query: UCIQueryParams) => {
  const dates = useDates(query);
  const page =
    typeof query.page === 'string' ? parseInt(query.page, 10) || 0 : 0;
  const perPage = getPageSizeFromQueryString(
    typeof query.pageSize === 'string' ? query.pageSize : undefined,
    DEFAULT_PER_PAGE
  );
  const sortBy = parseStringOrDefault(query.sortBy, SORT_START);
  const statusValue = parseNonEmptyString(query.status);
  const selectedStateValue = parseStringOrDefault(query.selectedState, '');
  const keywordValue = parseNonEmptyString(query.keyword);
  const view = query.interactionsView || query.view || View.List;
  const order = validSortOrder(query.sortDirection)
    ? query.sortDirection
    : SortDirectionEnum.Desc;

  return {
    page,
    perPage,
    sortBy,
    statusValue,
    selectedStateValue,
    keywordValue,
    view,
    order,
    ...dates,
  };
};

type Props = {
  className?: string;
  style?: React.CSSProperties;
  paymentAccountId?: string;
  channelId: string;
  sectionId: string;
  merchantAccountId?: string;
  additionalHeaders?: any;
};

export function UCIsV2({
  className,
  style,
  paymentAccountId,
  channelId,
  sectionId,
  merchantAccountId,
  additionalHeaders,
}: Props) {
  const [downloading, setDownloading] = useState(false);
  const [query, goToUrl, makeUrl] = useQueryString<UCIQueryParams>({
    page: 0,
    pageSize: DEFAULT_PER_PAGE,
    sortBy: SORT_START,
    sortDirection: SortDirectionEnum.Desc,
    selectedState: State.Any,
    interactionsView: View.List,
    keyword: '',
    status: '',
  });

  const {
    page,
    perPage,
    sortBy,
    keywordValue,
    view,
    order,
    startDate,
    endDate,
    startCreationDate,
    endCreationDate,
    statusValue,
  } = useParseQueryValues(query);

  useEffect(() => {
    setPage(page);
  }, [page]);

  const {
    loading,
    interactions,
    pageInfo,
    error,
    setPage,
    refresh,
    variables,
  } = useInteractionsOnChannelQuery({
    perPage,
    channelId,
    paymentAccountId,
    sectionId,
    merchantAccountId,
    keyword: query.keyword,
    selectedOrder: query.sortDirection,
    selectedSort: query.sortBy,
    selectedState: query.selectedState,
    selectedStatus: statusValue,
    startDate,
    endDate,
    startCreationDate,
    endCreationDate,
  });

  async function onDownload(
    payloadVariables: any = null,
    isCurrentPage: boolean = false
  ) {
    const downloadVariables = payloadVariables ?? variables;

    setDownloading(true);

    window.Alert.loading({
      title: 'Preparing…',
      message: '0%',
    });

    await pause(500);

    try {
      await downloadChannelInteractions({
        variables: downloadVariables,
        isCurrentPage,
      });
      window.Alert.hide();
    } catch (err) {
      window.Alert.hide();
      window.Alert.alert({
        title: `An error occurred downloading this file`,
        message: `See the error below and please try again`,
        error: err,
      });
    }

    setDownloading(false);
  }

  const exportAllResults = async () => {
    await onDownload();
  };

  const exportCurrentPageResults = async () => {
    await onDownload(variables, true);
  };

  const exportFilteredResults = async () => {
    await onDownload();
  };

  function handleSortClick(fieldToSort: string) {
    if (query.sortBy === fieldToSort) {
      const newOrder =
        query.sortDirection === SortDirectionEnum.Asc
          ? SortDirectionEnum.Desc
          : SortDirectionEnum.Asc;

      goToUrl({
        sortDirection: newOrder,
      });
    } else {
      goToUrl({ sortBy: fieldToSort });
    }
  }

  return (
    <div className={cx(styles.UCIs, className)} style={style}>
      <ControlMenu>
        <hr />
        {Object.values(View).map(v => (
          <Link data-test={`${v}-button`} key={v} to={makeUrl({ interactionsView: v })}>
            <IconButton icon={v} selected={view === v} inverted />
          </Link>
        ))}

        {view === View.Grid && (
          <>
            <Dropdown
              className={styles.dropDown}
              onValueChange={selectedState => goToUrl({ selectedState })}
              items={stateDropdownItems}
              isSearchable={false}
              value={query.selectedState}
            />
            <IconButton
              icon="download"
              loading={loading || downloading}
              onClick={() => onDownload(variables)}
            />
            <IconButton
              data-test="refresh-grid"
              icon="sync-alt"
              onClick={refresh}
              loading={loading || downloading}
            />
          </>
        )}
      </ControlMenu>
      {error && <ErrorMessage error={error} />}
      {view === View.List && (
        <UCITableV2
          sectionId={sectionId}
          interactions={interactions}
          selectedOrder={order}
          selectedSort={sortBy}
          additionalHeaders={additionalHeaders}
          pageSize={perPage}
          onRefresh={refresh}
          exportAllResults={exportAllResults}
          exportCurrentPageResults={exportCurrentPageResults}
          exportFilteredResults={exportFilteredResults}
          totalRows={pageInfo.total}
          loading={loading}
          keyword={keywordValue || ''}
        />
      )}
      {view === View.Grid && (
        <>
          <Pagination
            perPage={perPage}
            page={page}
            onPage={setPage}
            total={pageInfo.total}
          />
          <UCIList
            selectedSort={query.sortBy}
            selectedOrder={order}
            onSortClicked={handleSortClick}
            interactions={interactions}
          />
        </>
      )}
    </div>
  );
}
